import { useEffect, useRef, useState } from "react";
import type { AnswerCitation } from "@shared/types";
import { api, ApiError } from "../lib/apiClient";

interface SourceWorkspaceProps {
  citation: AnswerCitation;
  projectId: string;
  question: string;
  sourceIndex: number;
  sourceCount: number;
  onClose: () => void;
}

function cleanExcerpt(excerpt: string) {
  return excerpt.replace(/\s+/g, " ").trim();
}

export function SourceWorkspace({ citation, projectId, question, sourceIndex, sourceCount, onClose }: SourceWorkspaceProps) {
  const [activeTab, setActiveTab] = useState<"evidence" | "document">("evidence");
  const [pageImageUrl, setPageImageUrl] = useState<string | null>(null);
  const [documentUrl, setDocumentUrl] = useState<string | null>(null);
  const [pageLoading, setPageLoading] = useState(false);
  const [documentLoading, setDocumentLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // The raw object URL loadDocument/openExternally most recently created,
  // tracked separately from `documentUrl` (which may have a #page=N
  // fragment appended for the iframe) since revokeObjectURL needs the
  // exact string createObjectURL returned.
  const documentObjectUrlRef = useRef<string | null>(null);

  useEffect(() => {
    return () => {
      if (documentObjectUrlRef.current) URL.revokeObjectURL(documentObjectUrlRef.current);
    };
  }, []);

  useEffect(() => {
    let disposed = false;
    let objectUrl: string | null = null;

    async function loadPageImage() {
      if (citation.mimeType !== "application/pdf") return;
      setPageLoading(true);
      setError(null);
      try {
        const result = await api.getFile(`/api/projects/${projectId}/chunks/${citation.chunkId}/page-image`);
        if (disposed) return;
        const url = result.kind === "blob" ? URL.createObjectURL(result.blob) : result.url;
        if (result.kind === "blob") objectUrl = url;
        setPageImageUrl(url);
      } catch (err) {
        if (!disposed) setError(err instanceof ApiError ? err.message : "Could not load the cited page preview.");
      } finally {
        if (!disposed) setPageLoading(false);
      }
    }

    void loadPageImage();
    return () => {
      disposed = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [citation.chunkId, citation.mimeType, projectId]);

  useEffect(() => {
    function onKeyDown(event: KeyboardEvent) {
      if (event.key === "Escape") onClose();
    }
    document.addEventListener("keydown", onKeyDown);
    document.body.classList.add("source-workspace-open");
    return () => {
      document.removeEventListener("keydown", onKeyDown);
      document.body.classList.remove("source-workspace-open");
    };
  }, [onClose]);

  async function loadDocument() {
    if (documentUrl) {
      setActiveTab("document");
      return;
    }
    setDocumentLoading(true);
    setError(null);
    try {
      const result = await api.getFile(`/api/projects/${projectId}/documents/${citation.documentId}/file`);
      const baseUrl = result.kind === "blob" ? URL.createObjectURL(result.blob) : result.url;
      if (result.kind === "blob") documentObjectUrlRef.current = baseUrl;
      const url = citation.page ? `${baseUrl}#page=${citation.page}&zoom=page-width` : baseUrl;
      setDocumentUrl(url);
      setActiveTab("document");
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Could not load the original source document.");
    } finally {
      setDocumentLoading(false);
    }
  }

  async function openExternally() {
    // Reuse the already-loaded "Full document" blob instead of fetching and
    // creating a second one for the same file.
    if (documentObjectUrlRef.current) {
      const url = citation.page ? `${documentObjectUrlRef.current}#page=${citation.page}` : documentObjectUrlRef.current;
      window.open(url, "_blank", "noopener,noreferrer");
      return;
    }
    setDocumentLoading(true);
    setError(null);
    try {
      const result = await api.getFile(`/api/projects/${projectId}/documents/${citation.documentId}/file`);
      const baseUrl = result.kind === "blob" ? URL.createObjectURL(result.blob) : result.url;
      const url = citation.page ? `${baseUrl}#page=${citation.page}` : baseUrl;
      window.open(url, "_blank", "noopener,noreferrer");
      // This tab doesn't otherwise hold the document open (unlike
      // loadDocument's iframe), so nothing else would ever revoke it —
      // give the new tab time to actually load the blob first, then free it
      // rather than leaking it for the rest of the session.
      if (result.kind === "blob") setTimeout(() => URL.revokeObjectURL(baseUrl), 30_000);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Could not open the original source document.");
    } finally {
      setDocumentLoading(false);
    }
  }

  const reference = [citation.page ? `Page ${citation.page}` : null, citation.section ? `§${citation.section}` : null]
    .filter(Boolean)
    .join(" · ");

  return (
    <div className="source-workspace-backdrop" role="presentation" onMouseDown={(e) => {
      if (e.target === e.currentTarget) onClose();
    }}>
      <aside className="source-workspace" role="dialog" aria-modal="true" aria-label={`Source ${sourceIndex} of ${sourceCount}: ${citation.documentTitle}`}>
        <header className="source-workspace-header">
          <div>
            <div className="source-workspace-kicker">Source {sourceIndex} of {sourceCount}</div>
            <h2>{citation.documentTitle}</h2>
            {reference && <p>{reference}</p>}
          </div>
          <button type="button" className="source-workspace-close" onClick={onClose} aria-label="Close source viewer">×</button>
        </header>

        <div className="source-workspace-tabs" role="tablist" aria-label="Source viewer tabs">
          <button type="button" role="tab" aria-selected={activeTab === "evidence"} className={activeTab === "evidence" ? "active" : ""} onClick={() => setActiveTab("evidence")}>Evidence</button>
          <button type="button" role="tab" aria-selected={activeTab === "document"} className={activeTab === "document" ? "active" : ""} onClick={() => void loadDocument()} disabled={documentLoading}>{documentLoading ? "Loading document…" : "Full document"}</button>
        </div>

        <div className="source-workspace-body">
          {activeTab === "evidence" ? (
            <>
              <section className="source-preview-card">
                <div className="source-preview-toolbar">
                  <span>{citation.mimeType === "application/pdf" ? `Cited page${citation.page ? ` · ${citation.page}` : ""}` : "Source preview"}</span>
                  <button type="button" onClick={() => void openExternally()} disabled={documentLoading}>Open externally ↗</button>
                </div>
                <div className="source-preview-stage">
                  {pageLoading && <div className="source-preview-loading">Loading cited page…</div>}
                  {!pageLoading && pageImageUrl && <img src={pageImageUrl} alt={`Cited page from ${citation.documentTitle}`} />}
                  {!pageLoading && !pageImageUrl && citation.mimeType === "application/pdf" && <div className="source-preview-empty">Page preview is unavailable. Open the full document to inspect the source.</div>}
                  {citation.mimeType !== "application/pdf" && <div className="source-preview-empty">Use “Full document” to inspect this source inside CEI Nexus.</div>}
                </div>
              </section>

              <section className="source-evidence-panel">
                <div className="source-evidence-heading">
                  <div>
                    <span className="source-evidence-label">Supporting evidence</span>
                    <h3>Why this source supports the answer</h3>
                  </div>
                  <span className="source-evidence-reference">{reference || "Referenced source"}</span>
                </div>
                <p className="source-evidence-excerpt">{cleanExcerpt(citation.excerpt)}</p>
                <div className="source-evidence-question"><strong>Question:</strong> {question}</div>
              </section>
            </>
          ) : (
            <section className="source-document-frame-wrap">
              {documentUrl ? (
                citation.mimeType === "application/pdf" ? <iframe className="source-document-frame" src={documentUrl} title={citation.documentTitle} /> : <div className="source-preview-empty">This document type cannot be embedded here yet. Use “Open externally” to view the original file.</div>
              ) : (
                <div className="source-preview-loading">Loading document…</div>
              )}
            </section>
          )}

          {error && <p className="source-workspace-error">{error}</p>}
        </div>
      </aside>
    </div>
  );
}
